import { LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { provide } from '@lit/context'
import { StepSelectionData } from 'components/ordering/configurator/api/dtos/step-selection-data.js'
import { StepDataDTO } from 'components/ordering/configurator/api/dtos/step-data-dto.js'
import { SummaryDTO } from 'components/ordering/configurator/api/dtos/summary-dto.js'
import { VariantDataDTO } from 'components/ordering/configurator/api/dtos/variant-data-dto.js'
import { PostMultiStep } from 'components/ordering/configurator/api/requests/post-multi-step.js'
import { PostAddToBasket } from 'components/ordering/configurator/api/requests/post-add-to-basket.js'
import { executeConfiguratorRequest } from 'components/ordering/configurator/api/executor.js'
import { NotFoundError } from 'components/ordering/configurator/api/dtos/error.js'
import { AddToBasketSuccess, AddToBasketFailure } from 'components/ordering/add-to-basket/basket-parser.js'
import {
  addToBasketContext,
  configuratorAdditionalTextsContext,
  configuratorCompletedContext,
  configuratorDataContext,
  configuratorGroupsContext,
  configuratorProductContext,
  configuratorStepsContext,
  configuratorSummaryContext,
  configuratorUrlContext,
  configuratorVariantDataContext,
  NotFetchedYet,
  updateStepSelectionContext
} from 'components/ordering/configurator/components/context/context-definition.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { WINDOW_HISTORY, type WindowHistory } from '@cloudsuite/mosaic-shared/scripts/window-history.js'
import { GetPayload } from 'components/ordering/configurator/api/requests/get-payload.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { ProductAddedEvent } from 'components/ordering/add-to-basket/events.js'

@customElement('csc-configurator-context')
export class CSCConfiguratorContext extends LitElement {
  protected override createRenderRoot = () => this

  @property({ attribute: 'product-slug', type: String })
  public productSlug!: string

  @property({ attribute: 'product-id', type: Number })
  public productId!: number

  @provide({ context: configuratorProductContext })
  public product: { id: number } | NotFetchedYet = new NotFetchedYet()

  @provide({ context: configuratorSummaryContext })
  public summary: SummaryDTO | NotFetchedYet = new NotFetchedYet()

  @provide({ context: configuratorCompletedContext })
  public completed: boolean | NotFetchedYet = new NotFetchedYet()

  @provide({ context: configuratorStepsContext })
  public steps: StepDataDTO[] | NotFetchedYet = new NotFetchedYet()

  @provide({ context: configuratorDataContext })
  public data: Record<string, StepSelectionData> | NotFetchedYet = new NotFetchedYet()

  @provide({ context: configuratorGroupsContext })
  public groups: Record<string, boolean> | NotFetchedYet = new NotFetchedYet()

  @provide({ context: configuratorVariantDataContext })
  public variantData: VariantDataDTO | null | NotFetchedYet = new NotFetchedYet()

  @provide({ context: configuratorUrlContext })
  public url: string | null | NotFetchedYet = new NotFetchedYet()

  @provide({ context: configuratorAdditionalTextsContext })
  public additionalTexts: Record<string, string> | NotFetchedYet = new NotFetchedYet()

  @inject(WINDOW_HISTORY)
  private windowHistory!: WindowHistory

  @inject(WINDOW_LOCATION)
  private windowLocation!: WindowLocation

  @provide({ context: addToBasketContext })
  public addToBasket = async (): Promise<AddToBasketSuccess | AddToBasketFailure> => {
    if (this.product instanceof NotFetchedYet || !this.encodedUrlSegment) {
      throw new Error('Cannot add to basket: product or configuration not available')
    }

    const request = new PostAddToBasket({ productId: this.product.id, productConfiguration: this.encodedUrlSegment, })
    const response = await executeConfiguratorRequest(request)

    if (response instanceof AddToBasketSuccess) {
      this.dispatchEvent(new ProductAddedEvent(response.basketCount, response.itemId))
    }

    return response
  }

  @provide({ context: updateStepSelectionContext })
  public updateStepSelection = async (stepKey: string, stepData: StepSelectionData) => {
    const previousStepData = this.data instanceof NotFetchedYet ? {} : this.data
    await this.postMultiStep({
      ...previousStepData,
      [stepKey]: stepData
    })
  }

  get encodedUrlSegment() {
    const segments = this.windowLocation.pathname.split('/').filter(Boolean)
    const configuratorIndex = segments.indexOf('configurator')
    return segments[configuratorIndex + 1]
  }

  public payloadFetched?: Promise<void>

  private getPayload = async () => {
    const request = new GetPayload({ productSlug: this.productSlug, productId: this.productId, payload: this.encodedUrlSegment })
    const response = await executeConfiguratorRequest(request)

    await this.postMultiStep(response)
  }

  private postMultiStep = async (selections: Record<string, StepSelectionData>) => {
    const request = new PostMultiStep({ productSlug: this.productSlug, productId: this.productId, selections })
    const response = await executeConfiguratorRequest(request)

    if (response instanceof NotFoundError) throw response

    this.product = response.product
    this.summary = response.summary
    this.completed = response.completed
    this.steps = response.steps
    this.data = response.data
    this.groups = response.groups
    this.variantData = response.variantData
    this.url = response.url
    this.additionalTexts = response.additionalTexts

    if (response.url) {
      this.updateWindowLocationWithConfiguratorPayload(response.url)
    }
  }

  private updateWindowLocationWithConfiguratorPayload(url: string) {
    this.windowHistory.pushState(url)
  }

  override connectedCallback() {
    super.connectedCallback()
    this.payloadFetched = this.getPayload()
  }
}
